// React imports
import React, { createContext, FC, useRef, useMemo, useCallback } from 'react';

// Internal imports
import { getTemplates } from '@/node-editor/utilities';
import { AppenderDialog } from './appender-dialog';
import {
  HookFormDialog,
  IHookFormDialogOptions,
} from '@/components/hook-form-dialog';
import { INodeTemplate } from '@/types/node';

interface IProps {
  supports: 'page' | 'room';
  children: React.ReactNode;
}

interface IProvided {
  openAppender: (nodeId: string) => void;
  openSettings: (options: IHookFormDialogOptions) => void;
  templates: Record<string, INodeTemplate>;
}

// Simplified ref interfaces
interface DialogRef {
  open: (nodeId: string) => void;
}

interface SettingsRef {
  open: (options: IHookFormDialogOptions) => void;
}

export const NodeDialogContext = createContext<IProvided | undefined>(
  undefined
);

/**
 * Context provider for managing node dialogs (appender and settings)
 * @param supports - The context type this provider supports ('page' | 'room')
 */
export const NodeDialogProvider: FC<IProps> = ({ children, supports }) => {
  // Props validation
  if (!['page', 'room'].includes(supports)) {
    throw new Error(
      `Invalid supports value: ${supports}. Must be 'page' or 'room'`
    );
  }

  const appenderRef = useRef<DialogRef | null>(null);
  const settingsRef = useRef<SettingsRef | null>(null);

  // Memoized templates with error handling
  const allowedTemplates = useMemo(() => {
    try {
      return getTemplates()
        .filter((template) => template.supports?.contexts?.includes(supports))
        .reduce(
          (templates, template) => ({
            ...templates,
            [template.type]: template,
          }),
          {} as Record<string, INodeTemplate>
        );
    } catch (error) {
      console.error('Failed to load templates:', error);
      return {} as Record<string, INodeTemplate>;
    }
  }, [supports]);

  // Memoized callback functions with proper null checks
  const openAppender = useCallback((nodeId: string): void => {
    if (appenderRef.current) {
      appenderRef.current.open(nodeId);
    }
  }, []);

  const openSettings = useCallback((options: IHookFormDialogOptions): void => {
    console.log('openSettings', settingsRef.current, options);
    if (settingsRef.current) {
      settingsRef.current.open(options);
    }
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      templates: allowedTemplates,
      openAppender,
      openSettings,
    }),
    [allowedTemplates, openAppender, openSettings]
  );

  return (
    <NodeDialogContext.Provider value={contextValue}>
      {children}
      <HookFormDialog ref={settingsRef} />
      <AppenderDialog ref={appenderRef} templates={allowedTemplates} />
    </NodeDialogContext.Provider>
  );
};
